import {
  Button,
  Icon,
  PolaroidPhoto,
  ProfilePic,
  ImageEditor,
  Stack,
  Toast
} from '@collegebacker/backer-ui/ui'
import classNames from 'classnames'
import React, { useEffect, useRef, useState } from 'react'

import gql from 'graphql-tag'
import { ApolloError, useMutation } from '@apollo/react-hooks'
import { uploadPhoto } from 'utils/helpers'
import { minWidthOfScreen, isMobile } from 'utils'
import { ShareLinkModalNew } from 'components/modal/shareLinkModal/ShareLinkModalNew'

import { TextEditModal } from './TextEditModal'
import GreetingModal from '../gifting-flow/GreetingModal'

import styles from './FundSection.module.scss'

const MIN_CHAR_LENGTH = 160
const MAX_EL_HEIGHT = 144

const UPDATE_GIFTING_PAGE = gql`
  mutation UpdateGiftingPage($input: UpdateGiftingPageInputType!) {
    updateGiftingPage(input: $input) {
      id
      photoUrl
      ogPhotoUrl
      fundName
      description
    }
  }
`

const EditFloatingButton = () => {
  const [isShown, setIsShown] = useState(false)
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 })

  const triggerIds = ['fundname', 'greeting']

  useEffect(() => {
    const trigerElements = triggerIds.map((id) =>
      document.getElementById(id)
    ) as HTMLElement[]

    const handleMouseMove = (e: MouseEvent) => {
      const isMouseOverTrigger = trigerElements.some((el) =>
        el?.contains(e.target as Node)
      )

      setIsShown(isMouseOverTrigger)
      setCursorPosition({ x: e.clientX, y: e.clientY })
    }

    document.addEventListener('mousemove', handleMouseMove)
  }, [])

  return (
    <div
      className={classNames(styles.editFloatingButton, {
        [styles.editFloatingButtonShown]: isShown
      })}
      style={{
        top: `${cursorPosition.y}px`,
        left: `${cursorPosition.x}px`
      }}
    >
      <Icon name="edit" className={styles.iconEdit} />
    </div>
  )
}

const FundSection = ({
  disableFollowFund,
  fund,
  birthdayReminderModalRef
}: Props) => {
  const shareModalRef = useRef<any>(null)
  const greetingModalRef = useRef<any>(null)
  const toastRef = useRef<ToastRef>(null)

  const {
    canUpdateFund,
    countBackers,
    fundName,
    fundOwner,
    giftingPage: { ownerPhotoUrl, photoUrl, greeting }
  } = fund
  const [fundNameElementHeight, setFundNameElementHeight] = useState(0)

  const isMobileDevice = isMobile()

  // Fund photo related states
  const [fundPhotoUrl, setFundPhotoUrl] = useState(photoUrl || undefined)
  const [_ogPhotoUrl, setOgPhotoUrl] = useState(undefined)
  const [imageEditorFile, setImageEditorFile] = useState<string | undefined>(
    undefined
  )
  const [isEditorOpen, setIsEditorOpen] = useState<boolean>(false)

  // text content related states
  const [fundNameText, setFundNameText] = useState(fundName)
  const [isFundNameEditOpen, setIsFundNameEditOpen] = useState(false)

  const [greetingText, setGreetingText] = useState(greeting)
  const [isGreetingEditOpen, setIsGreetingEditOpen] = useState(false)

  const [updateGiftingPage] = useMutation(UPDATE_GIFTING_PAGE)

  // Check fundName element height to determine whether to
  // add the className to truncate and for hover state to show full string
  const fundNameEl = (document as any).getElementById('fundname')

  useEffect(() => {
    if (fundNameEl) setFundNameElementHeight(fundNameEl.offsetHeight)
  }, [fundNameEl])

  const uploadFundPhoto = async (image: string) => {
    const fundPhotoResponse = await uploadPhoto({
      image,
      preset: 'fund_photos',
      setUrl: setFundPhotoUrl
    })
    if (fundPhotoResponse.error?.message)
      return { errorMessage: fundPhotoResponse.error?.message }

    const uploadedPhotoUrl = fundPhotoResponse.secure_url
    const ogPhotoResponse = await uploadPhoto({
      image,
      preset: 'og_photos_template',
      setUrl: setOgPhotoUrl
    })

    const uploadedOgPhotoUrl = ogPhotoResponse.secure_url

    setIsEditorOpen(false)

    if (updateGiftingPage) {
      updateGiftingPage({
        variables: {
          input: {
            uuid: fund.uuid,
            photoUrl: uploadedPhotoUrl,
            ogPhotoUrl: uploadedOgPhotoUrl
          }
        }
      })
        .then(() => {
          toastRef?.current?.showToast('Photo updated', {
            type: 'success',
            closeOnClick: true,
            showCloseIcon: false,
            timeout: 3000
          })
        })
        .catch((error: ApolloError) => {
          console.error('upload fund image error', error)
          setFundPhotoUrl(photoUrl)
        })
    }
  }

  const handleUpdateFundName = (value: string) => {
    if (updateGiftingPage) {
      updateGiftingPage({
        variables: {
          input: {
            uuid: fund.uuid,
            fundName: value
          }
        }
      })
        .then(() => {
          setFundNameText(value)
          setIsFundNameEditOpen(false)

          toastRef?.current?.showToast('Fund name updated', {
            type: 'success',
            closeOnClick: true,
            showCloseIcon: false,
            timeout: 3000
          })
        })
        .catch((error: ApolloError) => {
          console.error('update fund name error', error)
          setFundNameText(fundName)
        })
    }
  }

  const handleUpdateGreeting = (value: string) => {
    if (updateGiftingPage) {
      updateGiftingPage({
        variables: {
          input: {
            uuid: fund.uuid,
            description: value
          }
        }
      })
        .then(() => {
          setGreetingText(value)
          setIsGreetingEditOpen(false)

          toastRef?.current?.showToast('Greeting updated', {
            type: 'success',
            closeOnClick: true,
            showCloseIcon: false,
            timeout: 3000
          })
        })
        .catch((error: ApolloError) => {
          console.error('update greeting error', error)
          setGreetingText(greeting)
        })
    }
  }

  const handleImageSelect = (imageFile: File) => {
    setIsEditorOpen(true)
    setImageEditorFile(URL.createObjectURL(imageFile))
  }

  return (
    <>
      {canUpdateFund && !isMobileDevice && (
        <>
          <EditFloatingButton />
          <Toast ref={toastRef} />
          <ImageEditor
            imageFile={imageEditorFile}
            isOpen={isEditorOpen}
            onSubmit={uploadFundPhoto}
            onClose={() => setIsEditorOpen(false)}
          />
          <TextEditModal
            text={fundNameText}
            onConfirm={(value) => handleUpdateFundName(value)}
            onCancel={() => setIsFundNameEditOpen(false)}
            isOpen={isFundNameEditOpen}
          />
          <TextEditModal
            text={greetingText}
            onConfirm={(value) => handleUpdateGreeting(value)}
            onCancel={() => setIsGreetingEditOpen(false)}
            isOpen={isGreetingEditOpen}
          />
        </>
      )}
      <section className={styles.fundSection}>
        <Stack>
          <div className={styles.profilePicBlock}>
            <PolaroidPhoto
              imageSrc={fundPhotoUrl}
              name={fundName}
              className={styles.photo}
              onChange={handleImageSelect}
              uploadMode={canUpdateFund}
            />
            <div>
              <h1
                id="fundname"
                className={classNames(styles.fundTitle, {
                  [styles.truncateFundName]:
                    fundNameElementHeight > MAX_EL_HEIGHT,
                  'typo-app-title-large': minWidthOfScreen(621),
                  'typo-app-title-small': !minWidthOfScreen(621)
                })}
                title={fundNameText}
                onClick={() => {
                  if (!isMobileDevice && canUpdateFund)
                    setIsFundNameEditOpen(true)
                }}
              >
                {fundNameText}
              </h1>
              <div className={styles.statsWrap}>
                {fund.childAgeLabel && (
                  <div className={styles.statsItem}>
                    <Icon name="b-cake" className={styles.iconGift} />
                    <span className="typo-app-body-caption">
                      {fund.childAgeLabel}
                    </span>
                  </div>
                )}
                {!disableFollowFund && (
                  <Button
                    className={classNames(
                      styles.followBtn,
                      styles.followBtnMobile
                    )}
                    label=""
                    mode="outline"
                    icon="calendar"
                    onClick={() => {
                      birthdayReminderModalRef?.current?.open()
                    }}
                  />
                )}
                <div className={styles.statsItemDesktop}>
                  <Icon name="people-group" className={styles.iconGift} />
                  <span className="typo-app-body-caption">
                    {countBackers} backer{countBackers > 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            </div>
            <Button
              className={styles.shareBtnDesktop}
              hasMinWidth={false}
              label="Share"
              size="small"
              mode="outline"
              icon="share-arrow"
              onClick={() => {
                shareModalRef?.current?.open()
              }}
            />
          </div>
        </Stack>
        <div className={styles.ownerContainer}>
          <div id="greeting">
            <p
              className={classNames('typo-app-body-paragraph', styles.greeting)}
              onClick={() => {
                if (!isMobileDevice && canUpdateFund)
                  setIsGreetingEditOpen(true)
              }}
            >
              {truncatedString({
                str: greetingText,
                maxLength: MIN_CHAR_LENGTH
              })}
            </p>
            <GreetingModal
              modalRef={greetingModalRef}
              photoUrl={ownerPhotoUrl}
              name={fundOwner.fullName}
              greeting={greetingText}
            />
            {greeting.length >= MIN_CHAR_LENGTH && (
              <button
                className={styles.greetingModalTrigger}
                onClick={() => {
                  greetingModalRef.current?.open()
                }}
              >
                <Icon name="opened-eye" className={styles.iconEye} />
              </button>
            )}
          </div>
          <div className={styles.ownerPhotoWrap}>
            <div className={styles.ownerPhotoContainer}>
              <ProfilePic
                imageSrc={ownerPhotoUrl}
                name={fundOwner.fullName}
                className={styles.photoSmall}
              />
              <p className="typo-app-body-caption">{fundOwner.fullName}</p>
            </div>
            <ShareLinkModalNew
              modalRef={shareModalRef}
              shareSource="gifting page"
              fund={fund}
            />
            <Button
              className={styles.shareBtnMobile}
              hasMinWidth={false}
              label="Share"
              size="small"
              mode="outline"
              icon="share-arrow"
              onClick={() => {
                shareModalRef?.current?.open()
              }}
            />
          </div>
        </div>
      </section>
    </>
  )
}

const truncatedString = ({
  str,
  maxLength
}: {
  str: string
  maxLength: number
}) => {
  const fundNameLength = str.length

  if (fundNameLength <= maxLength) return str

  const maxChars = str.slice(0, maxLength)
  const lastSpaceIndex = maxChars.lastIndexOf(' ')
  const truncatedStr = str.slice(0, lastSpaceIndex)

  return `${truncatedStr}...`
}

interface Props {
  disableFollowFund: boolean
  fund: {
    fundName: string
    handle: string
    uuid: string
    canUpdateFund: boolean
    countBackers: number
    childAgeLabel?: string
    fundOwner: {
      fullName: string
    }
    giftingPage: {
      ownerPhotoUrl: string
      photoUrl: string
      greeting: string
    }
  }
  birthdayReminderModalRef: React.RefObject<any>
}

export default FundSection
